/**
 * RBP: shade dropdown.
 *
 * Deliberately holds no state. Selecting a shade checks the matching radio
 * input in the existing variant picker and fires a bubbling `change`, which is
 * exactly what <product-variant-selects> already listens for. Everything
 * downstream -- variant swap, gallery filtering, price, URL -- runs unchanged.
 *
 * It also listens back, so choosing a shade via the chips keeps the closed bar
 * in sync.
 */
class ShadeDropdown extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector("[data-shade-dropdown-toggle]");
    this.list = this.querySelector("[data-shade-dropdown-list]");
    this.selectedLabel = this.querySelector("[data-shade-dropdown-selected-label]");
    this.selectedSwatch = this.querySelector("[data-shade-dropdown-selected-swatch]");
    this.picker = this.closest("product-variant-selects");

    if (!this.toggleButton || !this.list) return;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.toggleButton.addEventListener("click", () => this.toggle());

    this.querySelectorAll("[data-shade-dropdown-item]").forEach(item => {
      item.addEventListener("click", () => this.select(item.dataset.value));
    });

    // Keep in sync when the shade is changed from the swatch chips instead.
    if (this.picker) {
      this.picker.addEventListener("change", () => this.syncFromInputs());
    }

    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeyDown);
  }

  get isOpen() {
    return this.toggleButton.getAttribute("aria-expanded") === "true";
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.toggleButton.setAttribute("aria-expanded", "true");
    this.list.hidden = false;
  }

  close() {
    this.toggleButton.setAttribute("aria-expanded", "false");
    this.list.hidden = true;
  }

  onDocumentClick(event) {
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.close();
      this.toggleButton.focus();
    }
  }

  /** Find the radio for this value within the same option group. */
  findInput(value) {
    if (!this.picker) return null;
    const inputs = this.picker.querySelectorAll(".product-option__input");
    return (
      Array.from(inputs).find(input => input.value === value) || null
    );
  }

  select(value) {
    this.close();

    const input = this.findInput(value);
    if (!input || input.disabled) return;

    this.setSelectedState(value);

    if (input.checked) return;
    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /** Reflect the currently checked radio in the closed bar. */
  syncFromInputs() {
    if (!this.picker) return;
    const checked = this.picker.querySelector(".product-option__input:checked");
    if (checked) this.setSelectedState(checked.value);
  }

  setSelectedState(value) {
    if (this.selectedLabel) this.selectedLabel.textContent = value;

    let matchedItem = null;
    this.querySelectorAll("[data-shade-dropdown-item]").forEach(item => {
      const isMatch = item.dataset.value === value;
      item.classList.toggle("is-selected", isMatch);
      item.setAttribute("aria-selected", isMatch ? "true" : "false");
      if (isMatch) matchedItem = item;
    });

    // Reuse the list item's own swatch markup for the closed bar, so the two
    // can never drift apart.
    if (matchedItem && this.selectedSwatch) {
      const source = matchedItem.querySelector(".shade-dropdown__swatch");
      if (source) this.selectedSwatch.innerHTML = source.innerHTML;
    }
  }
}

if (!customElements.get("shade-dropdown")) {
  customElements.define("shade-dropdown", ShadeDropdown);
}
